/**
 * refreshDocument - the Document Editor's PERSONAL REFRESH
 * surface (Princewill's own-documents use case, approved
 * 2026-11-05): the owner uploads HIS OWN document (a PDF page or
 * a photo/scan of his resume/forms), and refreshes stale details
 * by hand - cover an old phone number with an opaque patch, type
 * the new one over it, drop in his logo/signature, print.
 *
 * This is the white-out + text-box mechanic every office editor
 * has, and it is deliberately ALL it is:
 *  - the upload becomes a BACKGROUND PICTURE and stays one - it
 *    is never analysed, never OCR'd, never routed through the
 *    protected clone engines, and nothing in it is detected,
 *    selected or removed by the software;
 *  - a "cover" is a plain opaque rectangle the USER places and
 *    sizes by hand (digital white-out: the patch stays visible
 *    as part of the document, exactly like physical white-out);
 *  - a "redact" block is the same idea in opaque black, for
 *    hiding a detail on a copy you share;
 *  - export flattens background + the user's overlays into one
 *    print-ready file at the background's own resolution.
 *
 * Geometry lives in page pixels (the stored background's pixel
 * size). Rotate/crop BAKE into the background (the stored image
 * is re-rendered) and transform the overlays with the pure
 * functions below, so screen, state and export can never disagree
 * about where an overlay sits.
 */
import { jsPDF } from 'jspdf';
import { saveBlobAsDownload } from './downloadFile';
import { fitWithin, downscaleImageFile } from './imageIngest';
import { renderPdfFirstPage } from './pdfFirstPage';

/** Long edge a refresh background is stored at. Bigger than the
 * photo-field ingest (900) because this image IS the document;
 * the strip fallbacks in the editor keep saves honest when even
 * this does not fit. */
export const REFRESH_INGEST_MAX_DIM = 1600;

/** The PDF export maps the background's pixels onto the page at
 * this density - stated in the export caption, never implied to
 * be more than it is. */
export const REFRESH_PDF_DPI = 150;

export type RefreshSourceKind = 'image' | 'pdf';

export interface RefreshBackground {
    /** The background picture (data URL). '' when a storage
     * fallback stripped it - the design then reopens with its
     * overlays and page size intact and asks for the file again. */
    src: string;
    /** Current page size in pixels (after any baked rotate/crop). */
    width: number;
    height: number;
    sourceKind: RefreshSourceKind;
    /** Percent, 100 = neutral. Render-time only (no geometry). */
    brightness: number;
    contrast: number;
    /** True when src was dropped by a storage fallback. */
    stripped?: boolean;
}

export type RefreshOverlayKind = 'text' | 'cover' | 'highlight' | 'redact' | 'image';

export interface RefreshOverlay {
    id: string;
    kind: RefreshOverlayKind;
    /** Page pixels (background coordinate space). */
    x: number; y: number; width: number; height: number;
    // text overlays
    text?: string;
    fontSize?: number;
    fontFamily?: string;
    bold?: boolean;
    color?: string;
    // cover patches
    fill?: string;
    // image overlays (library placements)
    src?: string;
    assetName?: string;
}

export interface RefreshState {
    name: string;
    background: RefreshBackground;
    overlays: RefreshOverlay[];
}

export const REFRESH_TEXT_FONTS = ['Arial', 'Georgia', 'Times New Roman', 'Courier New'];
export const REFRESH_HIGHLIGHT_FILL = '#FFEB3B';
export const REFRESH_HIGHLIGHT_ALPHA = 0.35;
export const REFRESH_REDACT_FILL = '#000000';

let overlayCounter = 0;
export function makeOverlayId(): string {
    overlayCounter += 1;
    return `ov-${Date.now().toString(36)}-${overlayCounter.toString(36)}`;
}

/** A new overlay of a kind, centred on the page at a sensible
 * starting size. The user moves/sizes it from there - placement
 * is always the user's hand, never a guess by the software. */
export function makeRefreshOverlay(kind: RefreshOverlayKind, page: { width: number; height: number }): RefreshOverlay {
    const w = Math.max(40, Math.round(page.width * (kind === 'text' ? 0.35 : 0.22)));
    const h = kind === 'text' ? Math.max(24, Math.round(page.height * 0.045))
        : kind === 'image' ? Math.round(w * 0.45)
        : Math.max(18, Math.round(page.height * 0.035));
    const base: RefreshOverlay = {
        id: makeOverlayId(), kind,
        x: Math.round((page.width - w) / 2), y: Math.round((page.height - h) / 2),
        width: w, height: h
    };
    if (kind === 'text') {
        return { ...base, text: '', fontSize: Math.max(12, Math.round(page.height * 0.028)), fontFamily: REFRESH_TEXT_FONTS[0], bold: false, color: '#111111' };
    }
    if (kind === 'cover') return { ...base, fill: '#FFFFFF' };
    if (kind === 'highlight') return { ...base, fill: REFRESH_HIGHLIGHT_FILL };
    if (kind === 'redact') return { ...base, fill: REFRESH_REDACT_FILL };
    return base; // image overlays are created by placeRefreshImage
}

export function placeRefreshImage(page: { width: number; height: number }, src: string, assetName: string, natural?: { width: number; height: number }): RefreshOverlay {
    const base = makeRefreshOverlay('image', page);
    if (natural && natural.width > 0 && natural.height > 0) {
        base.height = Math.max(16, Math.round(base.width * (natural.height / natural.width)));
        base.y = Math.round((page.height - base.height) / 2);
    }
    return { ...base, src, assetName };
}

// --- Pure geometry for baked background edits ---

/** Rotate the page 90 degrees. srcW/srcH are the CURRENT page
 * size; the overlays come back in the rotated page's space. */
export function rotateRefreshGeometry(
    overlays: RefreshOverlay[], srcW: number, srcH: number, dir: 'cw' | 'ccw'
): { width: number; height: number; overlays: RefreshOverlay[] } {
    const rotated = overlays.map(o => {
        if (dir === 'cw') {
            return { ...o, x: srcH - (o.y + o.height), y: o.x, width: o.height, height: o.width };
        }
        return { ...o, x: o.y, y: srcW - (o.x + o.width), width: o.height, height: o.width };
    });
    return { width: srcH, height: srcW, overlays: rotated };
}

/** Crop the page to a rect (page pixels). Overlays shift into the
 * cropped space, clamp at the edges, and drop when they fall
 * entirely outside. */
export function cropRefreshGeometry(
    overlays: RefreshOverlay[], crop: { x: number; y: number; width: number; height: number }
): { width: number; height: number; overlays: RefreshOverlay[] } {
    const kept: RefreshOverlay[] = [];
    for (const o of overlays) {
        const x1 = Math.max(o.x, crop.x), y1 = Math.max(o.y, crop.y);
        const x2 = Math.min(o.x + o.width, crop.x + crop.width), y2 = Math.min(o.y + o.height, crop.y + crop.height);
        if (x2 <= x1 || y2 <= y1) continue;
        kept.push({ ...o, x: x1 - crop.x, y: y1 - crop.y, width: x2 - x1, height: y2 - y1 });
    }
    return { width: crop.width, height: crop.height, overlays: kept };
}

/** Clamp an overlay inside the page (used after drags/resizes). */
export function clampOverlayToPage(o: RefreshOverlay, page: { width: number; height: number }): RefreshOverlay {
    const width = Math.min(Math.max(8, o.width), page.width);
    const height = Math.min(Math.max(8, o.height), page.height);
    return {
        ...o, width, height,
        x: Math.min(Math.max(0, o.x), page.width - width),
        y: Math.min(Math.max(0, o.y), page.height - height)
    };
}

// --- Image plumbing (injectable for tests) ---

export interface RefreshImageOps {
    loadImage: (src: string) => Promise<HTMLImageElement>;
    makeCanvas: (w: number, h: number) => HTMLCanvasElement;
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const timer = setTimeout(() => reject(new Error('Image load timed out')), 5000);
        img.onload = () => { clearTimeout(timer); resolve(img); };
        img.onerror = () => { clearTimeout(timer); reject(new Error('Could not decode the image')); };
        img.src = src;
    });
}

export const defaultRefreshImageOps: RefreshImageOps = {
    loadImage: loadImageElement,
    makeCanvas: (w, h) => { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; }
};

/** Downscale a data URL to a max long edge (JPEG), for ingest. */
export async function downscaleDataUrl(src: string, maxDim: number, ops: RefreshImageOps = defaultRefreshImageOps): Promise<{ src: string; width: number; height: number }> {
    const img = await ops.loadImage(src);
    const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
    const { width, height } = fitWithin(w, h, maxDim);
    if (width === w && height === h) return { src, width, height };
    const canvas = ops.makeCanvas(width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return { src, width: w, height: h };
    ctx.drawImage(img, 0, 0, width, height);
    try { return { src: canvas.toDataURL('image/jpeg', 0.85), width, height }; }
    catch { return { src, width: w, height: h }; }
}

/** Re-render the background picture rotated 90 degrees (the bake
 * behind rotateRefreshGeometry). PNG out: page pictures must not
 * pick up a second JPEG generation. */
export async function rotateImageDataUrl(src: string, dir: 'cw' | 'ccw', ops: RefreshImageOps = defaultRefreshImageOps): Promise<string> {
    const img = await ops.loadImage(src);
    const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
    const canvas = ops.makeCanvas(h, w);
    const ctx = canvas.getContext('2d');
    if (!ctx) return src;
    ctx.save();
    if (dir === 'cw') { ctx.translate(h, 0); ctx.rotate(Math.PI / 2); }
    else { ctx.translate(0, w); ctx.rotate(-Math.PI / 2); }
    ctx.drawImage(img, 0, 0);
    ctx.restore();
    return canvas.toDataURL('image/png');
}

/** Re-render the background picture cropped to a rect (the bake
 * behind cropRefreshGeometry). */
export async function cropImageDataUrl(src: string, crop: { x: number; y: number; width: number; height: number }, ops: RefreshImageOps = defaultRefreshImageOps): Promise<string> {
    const img = await ops.loadImage(src);
    const canvas = ops.makeCanvas(crop.width, crop.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return src;
    ctx.drawImage(img, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
    return canvas.toDataURL('image/png');
}

// --- Ingest: a file becomes a refresh background ---

export async function createRefreshFromImageFile(file: File): Promise<RefreshState> {
    const dataUrl = await downscaleImageFile(file, REFRESH_INGEST_MAX_DIM);
    const img = await loadImageElement(dataUrl);
    const width = img.naturalWidth || img.width, height = img.naturalHeight || img.height;
    return {
        name: file.name.replace(/\.[^.]+$/, '') || 'My document',
        background: { src: dataUrl, width, height, sourceKind: 'image', brightness: 100, contrast: 100 },
        overlays: []
    };
}

export async function createRefreshFromPdfFile(file: File): Promise<RefreshState> {
    const page = await renderPdfFirstPage(file);
    const stored = await downscaleDataUrl(page.dataUrl, REFRESH_INGEST_MAX_DIM);
    return {
        name: file.name.replace(/\.[^.]+$/, '') || 'My document',
        background: { src: stored.src, width: stored.width, height: stored.height, sourceKind: 'pdf', brightness: 100, contrast: 100 },
        overlays: []
    };
}

// --- Rendering (preview composition == export composition) ---

/** The CSS/canvas filter for the background's cleanup settings. */
export function refreshFilterCss(bg: RefreshBackground): string {
    const b = (bg.brightness ?? 100) / 100, c = (bg.contrast ?? 100) / 100;
    if (b === 1 && c === 1) return '';
    return `brightness(${b}) contrast(${c})`;
}

/** Load the background + every image overlay, keyed ('background' / overlay id). */
export async function loadRefreshImages(state: RefreshState, ops: RefreshImageOps = defaultRefreshImageOps): Promise<Map<string, HTMLImageElement>> {
    const images = new Map<string, HTMLImageElement>();
    const jobs: Array<Promise<void>> = [];
    if (state.background.src) {
        jobs.push(ops.loadImage(state.background.src).then(img => { images.set('background', img); }).catch(() => { /* missing art draws as paper */ }));
    }
    for (const o of state.overlays) {
        if (o.kind === 'image' && o.src) {
            jobs.push(ops.loadImage(o.src).then(img => { images.set(o.id, img); }).catch(() => { /* skip undrawable art */ }));
        }
    }
    await Promise.all(jobs);
    return images;
}

type Ctx2D = CanvasRenderingContext2D;

/**
 * Draw the flattened refresh document: the background picture
 * (with its brightness/contrast cleanup), then the user's
 * overlays in order. This is the ONLY composition the surface
 * produces - the screen preview mirrors it with DOM elements,
 * and both exports rasterize exactly this.
 */
export function drawRefresh(ctx: Ctx2D, state: RefreshState, images: Map<string, HTMLImageElement>, width: number, height: number): void {
    const page = state.background;
    const sx = width / Math.max(1, page.width);
    const sy = height / Math.max(1, page.height);

    ctx.save();
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    const bgImg = images.get('background');
    if (bgImg) {
        const filter = refreshFilterCss(page);
        if (filter) {
            try { (ctx as any).filter = filter; } catch { /* filter is an enhancement; draw without it */ }
        }
        ctx.drawImage(bgImg, 0, 0, width, height);
        try { (ctx as any).filter = 'none'; } catch { /* ignore */ }
    }
    ctx.restore();

    for (const o of state.overlays) {
        const x = o.x * sx, y = o.y * sy, w = o.width * sx, h = o.height * sy;
        ctx.save();
        if (o.kind === 'cover') {
            ctx.fillStyle = o.fill || '#FFFFFF';
            ctx.fillRect(x, y, w, h);
        } else if (o.kind === 'redact') {
            ctx.fillStyle = REFRESH_REDACT_FILL;
            ctx.fillRect(x, y, w, h);
        } else if (o.kind === 'highlight') {
            ctx.globalAlpha = REFRESH_HIGHLIGHT_ALPHA;
            ctx.fillStyle = o.fill || REFRESH_HIGHLIGHT_FILL;
            ctx.fillRect(x, y, w, h);
        } else if (o.kind === 'image') {
            const img = images.get(o.id);
            if (img) {
                const iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
                if (iw && ih) {
                    const scale = Math.min(w / iw, h / ih);
                    const dw = iw * scale, dh = ih * scale;
                    ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
                }
            }
        } else if (o.kind === 'text') {
            if (o.text) {
                const fontSize = (o.fontSize || 16) * sy;
                ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();
                ctx.font = `${o.bold ? 'bold ' : ''}${fontSize}px ${o.fontFamily || 'Arial'}, sans-serif`;
                ctx.fillStyle = o.color || '#111111';
                ctx.textBaseline = 'top';
                const lines = String(o.text).split('\n');
                lines.forEach((line, i) => { ctx.fillText(line, x + 2 * sx, y + 2 * sy + i * fontSize * 1.25); });
            }
        }
        ctx.restore();
    }
}

export async function renderRefreshCanvas(
    state: RefreshState,
    canvasFactory: (w: number, h: number) => HTMLCanvasElement = (w, h) => { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; },
    ops: RefreshImageOps = defaultRefreshImageOps
): Promise<HTMLCanvasElement> {
    const { width, height } = state.background;
    const canvas = canvasFactory(width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas is not available in this browser');
    const images = await loadRefreshImages(state, ops);
    drawRefresh(ctx as Ctx2D, state, images, width, height);
    return canvas;
}

/** The honest export facts for a refresh document: the file IS
 * the background at its own pixel size - never upscaled, never
 * described as print-DPI it does not have. */
export function refreshExportFacts(state: RefreshState): { widthPx: number; heightPx: number; pdfDpi: number; sizeMm: { w: number; h: number } } {
    const { width, height } = state.background;
    return {
        widthPx: width,
        heightPx: height,
        pdfDpi: REFRESH_PDF_DPI,
        sizeMm: { w: Math.round((width / REFRESH_PDF_DPI) * 25.4 * 10) / 10, h: Math.round((height / REFRESH_PDF_DPI) * 25.4 * 10) / 10 }
    };
}

export function refreshExportFilename(state: RefreshState, ext: 'png' | 'pdf'): string {
    const base = (state.name || 'my-document').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'my-document';
    return `${base}-refreshed.${ext}`;
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
        if (typeof canvas.toBlob === 'function') {
            canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Could not encode the image'))), 'image/png');
        } else {
            try {
                const url = canvas.toDataURL('image/png');
                const bin = atob(url.split(',')[1] || '');
                const bytes = new Uint8Array(bin.length);
                for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
                resolve(new Blob([bytes], { type: 'image/png' }));
            } catch (e) { reject(e as Error); }
        }
    });
}

export interface RefreshExportResult { filename: string; widthPx: number; heightPx: number; pdfDpi: number; sizeMm: { w: number; h: number } }

export async function exportRefreshPng(
    state: RefreshState,
    canvasFactory?: (w: number, h: number) => HTMLCanvasElement,
    ops?: RefreshImageOps
): Promise<RefreshExportResult> {
    const canvas = await renderRefreshCanvas(state, canvasFactory, ops);
    const blob = await canvasToBlob(canvas);
    const filename = refreshExportFilename(state, 'png');
    await saveBlobAsDownload(blob, filename);
    return { filename, ...refreshExportFacts(state) };
}

export async function exportRefreshPdf(
    state: RefreshState,
    canvasFactory?: (w: number, h: number) => HTMLCanvasElement,
    ops?: RefreshImageOps
): Promise<RefreshExportResult> {
    const canvas = await renderRefreshCanvas(state, canvasFactory, ops);
    const facts = refreshExportFacts(state);
    const pdf = new jsPDF({
        orientation: facts.sizeMm.w >= facts.sizeMm.h ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [facts.sizeMm.w, facts.sizeMm.h]
    });
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, facts.sizeMm.w, facts.sizeMm.h);
    const blob = pdf.output('blob') as Blob;
    const filename = refreshExportFilename(state, 'pdf');
    await saveBlobAsDownload(blob, filename);
    return { filename, ...facts };
}

/** Storage-fallback strip: drop the background picture (the one
 * bulky part) and keep the page size + overlays, marked stripped
 * so a restore asks for the file again instead of pretending the
 * document is whole. Mirrors stripDocImages' honesty contract. */
export function stripRefreshBackground(state: RefreshState): RefreshState {
    if (!state.background.src) return state;
    return { ...state, background: { ...state.background, src: '', stripped: true } };
}
